"use client";
import React from "react";
import { twMerge } from "tailwind-merge";
import type { DeviceState } from "../../types/DeviceStates";
import type { GridState } from "../../types/GridStates";

export interface BadgeProps {
  /** The content to display inside the badge */
  children: React.ReactNode;
  /** Visual variant of the badge */
  variant?:
    | "default"
    | "success"
    | "error"
    | "warning"
    | "info"
    | "primary"
    | "brand"
    | "secondary"
    | "neutral"
    | DeviceState
    | GridState;
  /** Size of the badge */
  size?: "xs" | "sm" | "md" | "lg";
  /** Shape of the badge */
  shape?: "pill" | "rounded" | "square";
  /** Whether to show a dot indicator */
  dot?: boolean;
  /** Position of the dot */
  dotPosition?: "left" | "right";
  /** Whether the dot should pulse (animated) */
  dotPulse?: boolean;
  /**
   * Native tooltip text.
   *
   * A badge is often an abbreviation of something the reader cannot infer — a state,
   * a threshold, a data-quality caveat. Without this, callers wrap the badge in a bare
   * `<span title>` purely to explain it, which adds a layout box around an inline-flex
   * element for no visual reason.
   */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

const badgeVariantStyles: Record<string, string> = {
  // Standard variants
  default: "bg-feedback-info-background text-feedback-info-text", // Non-action compatibility default; prefer info/neutral/primary/secondary in new code.
  success: "bg-feedback-success-background text-feedback-success-text",
  error: "bg-feedback-error-background text-feedback-error-text",
  warning: "bg-feedback-warning-background text-feedback-warning-text",
  info: "bg-feedback-info-background text-feedback-info-text",
  primary: "bg-action-primary/10 text-action-primary border border-action-primary/20",
  brand: "bg-action-primary/10 text-action-primary border border-action-primary/20", // Deprecated alias for primary
  secondary: "bg-action-secondary text-[color:var(--color-action-secondary-text)] border border-border-muted",
  neutral: "bg-background-muted text-text-secondary", // Neutral gray for inactive/false states

  // Device state variants
  charging: "bg-state-charging text-state-charging-text",
  discharging: "bg-state-discharging text-state-discharging-text",
  heat: "bg-state-heat text-state-heat-text",
  cool: "bg-state-cool text-state-cool-text",
  eco: "bg-state-eco text-state-eco-text",
  auto: "bg-state-auto text-state-auto-text",
  on: "bg-state-on text-state-on-text",
  off: "bg-state-off text-state-off-text",
  idle: "bg-state-idle text-state-idle-text",
  // Its tokens were generated long before it had a variant here, so a thermostat
  // reporting `circulate` resolved to no class at all — an unstyled badge.
  circulate: "bg-state-circulate text-state-circulate-text",
  unknown: "bg-state-unknown text-state-unknown-text",
  connected: "bg-feedback-success-background text-feedback-success-text",
  disconnected: "bg-feedback-error-background text-feedback-error-text",

  // Grid state variants
  importing: "bg-state-importing text-state-importing-text",
  exporting: "bg-state-exporting text-state-exporting-text",
};

const badgeSizeStyles = {
  xs: "px-1.5 py-0.5 text-xs",
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-3.5 py-2 text-sm",
  lg: "px-4 py-2.5 text-base",
};

const badgeShapeStyles = {
  pill: "rounded-full",
  rounded: "rounded-lg",
  square: "rounded-sm",
};

const dotSizeStyles = {
  xs: "w-1 h-1",
  sm: "w-1.5 h-1.5",
  md: "w-2 h-2",
  lg: "w-2.5 h-2.5",
};

/**
 * Badge
 *
 * A non-interactive label for displaying status, counts, or categories.
 * Use for read-only indicators. For interactive elements, use Chip instead.
 *
 * Supports device states (charging, discharging, heat, cool, circulate, etc.) and
 * grid states (importing, exporting, idle, unknown) as variant values.
 */
export function Badge({
  children,
  variant = "default",
  size = "sm",
  shape = "square",
  dot = false,
  dotPosition = "left",
  dotPulse = false,
  title,
  className,
}: BadgeProps) {
  const dotElement = dot && (
    <span
      className={twMerge("rounded-full bg-current opacity-75", dotSizeStyles[size], dotPulse && "animate-pulse")}
      aria-hidden="true"
    />
  );

  return (
    <span
      title={title}
      className={twMerge(
        "inline-flex items-center gap-1.5 font-medium",
        badgeSizeStyles[size],
        badgeShapeStyles[shape],
        badgeVariantStyles[variant],
        className
      )}
    >
      {dot && dotPosition === "left" && dotElement}
      {children}
      {dot && dotPosition === "right" && dotElement}
    </span>
  );
}
